import { useTranslation } from 'next-i18next';
import { ComponentProps, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Box, ButtonV2, RadioToggleV2 } from '@bridebook/ui';
import {
  useRsvpData,
  useRsvpForms,
  useRsvpNavigation,
  useRsvpState,
} from '../../guest-rsvp.context';
import GuestRsvpHeader from '../../partials/header';
import SubmitButtonDesktopWrapper from '../../partials/submit-button-desktop-wrapper';
import SubmitButtonMobileWrapper from '../../partials/submit-button-mobile-wrapper';
import componentStyles from '../substep.style';
import { getThemeProperties } from '@bridebook/web/lib/wedding-website/utils/get-theme-properties';
import getEventDetailsCaption from '@bridebook/web/lib/rsvp/utils/get-event-details-caption';

interface IContinueButtonProps {
  isDisabled: boolean;
  styles: any;
  onContinue: ComponentProps<typeof ButtonV2>['onClick'];
  children: React.ReactNode;
}

const ContinueButton: React.FC<IContinueButtonProps> = ({
  isDisabled,
  styles,
  onContinue,
  children,
}) => (
  <ButtonV2 disabled={isDisabled} styleWrapper={styles.continueButton} onClick={onContinue}>
    {children}
  </ButtonV2>
);

const EventsStep = () => {
  const { t } = useTranslation('weddingWebsite');
  const { eventsForm, nameForm } = useRsvpForms();
  const { setStep } = useRsvpNavigation();
  const { rsvpSubmissionStatus, setRsvpSubmissionStatus, isPreview } = useRsvpState();
  const { enrichedEvents, theme } = useRsvpData();

  const themeProperties = useMemo(() => getThemeProperties(theme), [theme]);
  const styles = useMemo(
    () => componentStyles(themeProperties, isPreview),
    [themeProperties, isPreview],
  );

  const { control } = eventsForm;
  const guestName = nameForm.getValues('fullName');

  useEffect(() => {
    if (rsvpSubmissionStatus === 'RESUBMISSION') {
      setRsvpSubmissionStatus('FIRST_SUBMISSION');
    }
    eventsForm.trigger();
  }, [eventsForm, rsvpSubmissionStatus, setRsvpSubmissionStatus]);

  const handleContinue: ComponentProps<typeof ButtonV2>['onClick'] = () => {
    setStep('RSVP_FORM.QUESTIONS');
  };

  return (
    <>
      <Box style={styles.substepWrapper}>
        <GuestRsvpHeader />
        <Box style={styles.events.nameWrapper}>
          <Box style={styles.events.name}>{guestName}</Box>
          <Box>{t('rsvp.guest.events.instruction')}</Box>
        </Box>
        <Box style={styles.events.listTitle}>{t('rsvp.guest.events.listTitle')}</Box>
        <Box style={styles.events.listWrapper}>
          {enrichedEvents.map((event) => (
            <Box key={event.eventId} style={styles.events.listItem}>
              <Controller
                control={control}
                name={event.eventId}
                render={({ field }) => (
                  <>
                    <Box style={styles.events.listItemName}>
                      <Box style={styles.events.listItemNameEvent}>{event.data?.name}</Box>
                      <Box style={styles.events.listItemNameCaption}>
                        {getEventDetailsCaption(event, t)}
                      </Box>
                    </Box>
                    <Box style={styles.events.listItemControls}>
                      <RadioToggleV2
                        id={`${event.eventId}-yes`}
                        name={event.eventId}
                        value="yes"
                        checked={field.value === 'yes'}
                        onChange={() => {
                          field.onChange('yes');
                          field.onBlur();
                        }}
                        label={t('rsvp.guest.events.answer.yes')}
                        wrapperStyle={styles.events.listItemControlsButton}
                      />
                      <RadioToggleV2
                        id={`${event.eventId}-no`}
                        name={event.eventId}
                        value="no"
                        checked={field.value === 'no'}
                        onChange={() => {
                          field.onChange('no');
                          field.onBlur();
                        }}
                        label={t('rsvp.guest.events.answer.no')}
                        wrapperStyle={styles.events.listItemControlsButton}
                      />
                    </Box>
                  </>
                )}
              />
            </Box>
          ))}
        </Box>
        <SubmitButtonDesktopWrapper>
          <ContinueButton
            isDisabled={!eventsForm.formState.isValid}
            styles={styles}
            onContinue={handleContinue}>
            {t('rsvp.common.continue')}
          </ContinueButton>
        </SubmitButtonDesktopWrapper>
      </Box>
      <SubmitButtonMobileWrapper>
        <ContinueButton
          isDisabled={!eventsForm.formState.isValid}
          styles={styles}
          onContinue={handleContinue}>
          {t('rsvp.common.continue')}
        </ContinueButton>
      </SubmitButtonMobileWrapper>
    </>
  );
};

export default EventsStep;
